import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Check, CheckCircle, CreditCard, ArrowRight, ArrowLeft, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { sanitizeDashboardProviderCopy } from '@/lib/providerCopy';
import { resolvePreselectedPlan } from '@/lib/planPreselect';
import { planCopy, ANNUAL_ONLY_PLAN_NAMES } from '@/lib/orderPlanCatalog';

/**
 * Structured error body returned by /api/billing/reactivate (and upgrade).
 * Mirrors server/lib/billingErrors.ts. queryClient.ts parks the parsed
 * JSON at error.data when throwIfResNotOk throws, so `code` below is read
 * as `err.data.code` in onError (NOT `err.code` — that would be the
 * top-level Error's undefined property).
 */
interface BillingErrorBody {
  code: string;
  message: string;
  requestId: string;
  details?: {
    clientSecret?: string;
    paymentIntentId?: string;
    setupIntentClientSecret?: string;
    message?: string;
    declineCode?: string;
    lifecycle?: string;
    subscription?: unknown;
  };
}

const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
export const isStripeConfigured = Boolean(stripePublicKey);

if (!isStripeConfigured) {
  console.error('[Stripe] VITE_STRIPE_PUBLIC_KEY environment variable is missing');
}

export const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : Promise.resolve(null);

interface Plan {
  id: string;
  name: string;
  description: string;
  basePrice: string;
  billingInterval: string;
  features: string[];
  limitations: Record<string, any>;
  isPopular: boolean;
  maxPhoneNumbers?: number;
  maxUsers?: number;
  maxSmsPerMonth?: number;
  maxVoiceMinutesPerMonth?: number;
}

function formatMoney(amount: number, currency = 'USD'): string {
  const normalizedCurrency = currency.toUpperCase();
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: normalizedCurrency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function getMonthlyDisplayPrice(plan: Pick<Plan, 'basePrice' | 'billingInterval'> | null | undefined): number {
  if (!plan) return 0;
  const price = parseFloat(String(plan.basePrice || '0'));
  return plan.billingInterval === 'yearly' ? price / 12 : price;
}

function getPlanPositioning(planName: string, fallback: string): string {
  const copy = planCopy(planName);
  if (copy) return copy.eyebrow.charAt(0) + copy.eyebrow.slice(1).toLowerCase();
  const normalizedName = planName.toLowerCase();

  if (normalizedName.includes('lite')) {
    return 'Lean essentials for a first number, inbound calls, and light SMS usage.';
  }

  if (normalizedName.includes('starter')) {
    return 'Best first choice for small teams that need local plus international reach.';
  }

  if (normalizedName.includes('growth')) {
    return 'Built for higher call volume, bigger SMS needs, and expanding teams.';
  }

  return sanitizeDashboardProviderCopy(fallback);
}

function getPlanOutcome(planName: string): string {
  const normalizedName = planName.toLowerCase();

  if (normalizedName.includes('lite')) return 'Launch affordably';
  if (normalizedName.includes('starter')) return 'Start selling faster';
  if (normalizedName.includes('growth')) return 'Scale with confidence';
  return 'Flexible plan';
}

function getPlanCta(planName: string, isUpgrade: boolean, isDowngrade: boolean): string {
  if (isUpgrade) return `Upgrade to ${planName}`;
  if (isDowngrade) return `Move to ${planName}`;
  return `Choose ${planName}`;
}

function getYearlySavingsAmount(group: GroupedPlan): number | null {
  if (!group.monthlyPlan || !group.yearlyPlan) return null;
  const monthlyAnnualPrice = parseFloat(String(group.monthlyPlan.basePrice || '0')) * 12;
  const yearlyPrice = parseFloat(String(group.yearlyPlan.basePrice || '0'));
  if (!monthlyAnnualPrice || !yearlyPrice || yearlyPrice >= monthlyAnnualPrice) return null;
  return Math.round((monthlyAnnualPrice - yearlyPrice) * 100) / 100;
}

/** Card order matches the public pricing page. Unknown plans go last, by price. */
const PLAN_DISPLAY_ORDER = ['Lite', 'Starter', 'Growth'];

const FEATURE_PREVIEW_COUNT = 5;

interface UpgradeContext {
  requiresUpgrade: boolean;
  organization: any;
  currentSubscription: any;
  availablePlans: Plan[];
  paymentMethods: any[];
  stripeCustomerId?: string;
  trialStatus?: 'active' | 'expired' | 'none';
}

interface GroupedPlan {
  name: string;
  description: string;
  monthlyPlan: Plan | null;
  yearlyPlan: Plan | null;
  features: string[];
  isPopular: boolean;
  tier: number;
  maxPhoneNumbers?: number;
  maxUsers?: number;
}

function groupPlansByName(plans: Plan[]): GroupedPlan[] {
  const grouped: Record<string, GroupedPlan> = {};
  
  plans.forEach(plan => {
    const baseName = plan.name.replace(/ (Monthly|Yearly|Annual)$/i, '').trim();
    
    if (!grouped[baseName]) {
      grouped[baseName] = {
        name: baseName,
        description: plan.description,
        monthlyPlan: null,
        yearlyPlan: null,
        features: plan.features || [],
        isPopular: plan.isPopular,
        tier: getMonthlyDisplayPrice(plan),
        maxPhoneNumbers: plan.maxPhoneNumbers,
        maxUsers: plan.maxUsers,
      };
    }
    
    if (plan.billingInterval === 'monthly') {
      grouped[baseName].monthlyPlan = plan;
    } else {
      grouped[baseName].yearlyPlan = plan;
    }
    
    if (plan.features?.length > grouped[baseName].features.length) {
      grouped[baseName].features = plan.features;
    }
  });
  
  const rank = (name: string) => {
    const i = PLAN_DISPLAY_ORDER.indexOf(name);
    return i === -1 ? PLAN_DISPLAY_ORDER.length : i;
  };

  // Marketing copy (features, popular badge) comes from the shared pricing
  // catalog so the in-app picker matches limephone.io. Prices and plan ids
  // still come from the subscription_plans rows used for checkout.
  return Object.values(grouped)
    .map((group) => {
      const copy = planCopy(group.name);
      if (!copy) return group;
      return {
        ...group,
        features: [copy.chip, copy.featuresHeading, ...copy.features],
        isPopular: !!copy.badge,
      };
    })
    .sort((a, b) => rank(a.name) - rank(b.name) || a.tier - b.tier);
}

export function PaymentMethodForm({
  onPaymentReady,
  onError,
  processing
}: {
  onPaymentReady: (paymentMethodId: string) => void;
  onError: (error: string) => void;
  processing: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isReady, setIsReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      onError('Payment system not loaded');
      return;
    }

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard`
      },
      redirect: 'if_required'
    });

    if (error) {
      onError(error.message || 'Payment setup failed');
    } else if (setupIntent?.payment_method) {
      onPaymentReady(setupIntent.payment_method as string);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
        <PaymentElement 
          onReady={() => setIsReady(true)}
          options={{ layout: 'tabs' }}
        />
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <Shield className="w-4 h-4" />
        <span>Secured by Stripe. Your card details are encrypted.</span>
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 text-base font-medium"
        disabled={!isReady || !stripe || processing}
      >
        {processing ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-4 h-4 mr-2" />
            Add Payment Method
          </>
        )}
      </Button>
    </form>
  );
}

/**
 * Accent classes. `default` is the dashboard's green; `brand` is the LimePhone
 * brand palette (tokens in index.css / tailwind `brand-*`) used when the modal
 * is opened from the mobile app. The modal surface is light-only, so lime
 * surfaces always pair with ink text for contrast.
 */
const TONES = {
  default: {
    cta: 'bg-green-600 text-white hover:bg-green-700',
    ctaDisabled: 'disabled:bg-green-600',
    spinner: 'border-white/30 border-t-white',
    toggleOn: 'bg-green-600 text-white',
    toggleSaveOn: 'text-green-100',
    toggleSaveOff: 'text-green-600',
    currentRing: 'ring-2 ring-green-500',
    currentBadge: 'bg-green-600 text-white',
    accentText: 'text-green-600',
    link: 'text-green-700',
    check: 'text-green-500',
    softPanel: 'bg-green-50 ring-1 ring-green-200',
    softNote: 'bg-green-50 text-green-800',
    successIconBg: 'bg-green-100',
    successIcon: 'text-green-600',
  },
  brand: {
    cta: 'bg-brand-lime text-brand-ink hover:bg-brand-lime-hover',
    ctaDisabled: 'disabled:bg-brand-lime',
    spinner: 'border-brand-ink border-t-transparent',
    toggleOn: 'bg-brand-lime text-brand-ink',
    toggleSaveOn: 'text-brand-ink',
    toggleSaveOff: 'text-brand-ink',
    currentRing: 'ring-2 ring-brand-ink',
    currentBadge: 'bg-brand-ink text-brand-lime',
    accentText: 'text-brand-ink',
    link: 'text-brand-ink underline decoration-brand-lime decoration-2 underline-offset-2',
    check: 'text-brand-forest',
    softPanel: 'bg-brand-lime-soft ring-1 ring-brand-lime',
    softNote: 'bg-brand-lime-soft text-brand-lime-soft-foreground',
    successIconBg: 'bg-brand-lime-soft',
    successIcon: 'text-brand-forest',
  },
} as const;
type Tone = (typeof TONES)[keyof typeof TONES];

function ReviewStep({
  isOpen,
  onBack,
  selectedPlan,
  currentPlan,
  hasPaymentMethod,
  onConfirm,
  processing,
  tone = TONES.default,
}: {
  tone?: Tone;
  isOpen: boolean;
  onBack: () => void;
  selectedPlan: Plan | null;
  currentPlan: Plan | null;
  hasPaymentMethod: boolean;
  onConfirm: (planId: string, paymentMethodId?: string) => void;
  processing: boolean;
}) {
  const [setupIntentSecret, setSetupIntentSecret] = useState<string | null>(null);
  const [needsPayment, setNeedsPayment] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  // Latch so the auto-fire useEffect runs once per captured PM. Without it,
  // a re-render after onSuccess could re-trigger the mutation.
  const [autoConfirmFired, setAutoConfirmFired] = useState(false);
  const { toast } = useToast();

  const setupIntentMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/billing/setup-intent'),
    onSuccess: (data: { client_secret: string }) => {
      setSetupIntentSecret(data.client_secret);
    },
    onError: () => {
      toast({
        title: "Payment Setup Failed",
        description: "Please try again.",
        variant: "destructive"
      });
    }
  });

  useEffect(() => {
    // Show payment form if no payment method on file (regardless of stripeCustomerId)
    if (isOpen && !hasPaymentMethod) {
      setNeedsPayment(true);
      setupIntentMutation.mutate();
    } else {
      setNeedsPayment(false);
    }
  }, [isOpen, hasPaymentMethod]);

  // Exact proration from Stripe (via /api/billing/upgrade-preview). Tolerates
  // failure: if the preview errors we fall back to generic copy and never
  // block the upgrade path.
  const { data: preview, isLoading: previewLoading } = useQuery<{
    amountDue: number;
    currency: string;
    prorationAmount: number;
    nextBillingDate: string | null;
  }>({
    queryKey: ['/api/billing/upgrade-preview', selectedPlan?.id],
    queryFn: async () =>
      apiRequest('GET', `/api/billing/upgrade-preview?planId=${encodeURIComponent(selectedPlan!.id)}`),
    enabled: isOpen && !!selectedPlan?.id,
    staleTime: 30_000,
    retry: false,
  });

  // Auto-fire confirm as soon as a freshly-captured payment method lands, so
  // adding a card goes straight to the plan change. Latched against re-renders.
  // Hooks must run before the `if (!selectedPlan) return null` guard below
  // (React error #310 otherwise).
  useEffect(() => {
    if (needsPayment && paymentMethodId && !processing && !autoConfirmFired && selectedPlan) {
      setAutoConfirmFired(true);
      onConfirm(selectedPlan.id, paymentMethodId);
    }
  }, [needsPayment, paymentMethodId, processing, autoConfirmFired, selectedPlan, onConfirm]);

  useEffect(() => {
    if (!isOpen || !paymentMethodId) setAutoConfirmFired(false);
  }, [isOpen, paymentMethodId]);

  if (!selectedPlan) return null;

  const selectedPrice = getMonthlyDisplayPrice(selectedPlan);
  const currentPrice = getMonthlyDisplayPrice(currentPlan);
  const isDowngrade = selectedPrice < currentPrice && currentPrice > 0;
  const diff = selectedPrice - currentPrice;
  const currency = preview?.currency || 'USD';
  const nextDate = preview?.nextBillingDate ? new Date(preview.nextBillingDate).toLocaleDateString() : null;

  const handleConfirm = () => {
    if (needsPayment && paymentMethodId) {
      onConfirm(selectedPlan.id, paymentMethodId);
    } else {
      onConfirm(selectedPlan.id);
    }
  };

  const options = setupIntentSecret ? {
    clientSecret: setupIntentSecret,
    appearance: { theme: 'stripe' as const },
  } : null;

  const planLine = (label: string, plan: Plan | null, price: number, highlight = false) => (
    <div className={`flex-1 rounded-xl p-3 ${highlight ? tone.softPanel : 'bg-gray-50'}`}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-0.5 font-semibold text-gray-900">{plan?.name || 'No plan'}</p>
      <p className="text-sm text-gray-500">
        {formatMoney(price)}/mo{plan?.billingInterval === 'yearly' && ' · billed yearly'}
      </p>
    </div>
  );

  return (
    <div className="p-5 sm:p-6 space-y-5">
      <button
        type="button"
        onClick={onBack}
        disabled={processing}
        className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50"
      >
        <ArrowLeft className="h-4 w-4" /> All plans
      </button>

      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          {isDowngrade ? 'Review your downgrade' : 'Review your plan change'}
        </h2>
        <p className="mt-1 text-sm text-gray-500">Check the details, then confirm.</p>
      </div>

      <div className="flex items-center gap-2">
        {planLine('Current', currentPlan, currentPrice)}
        <ArrowRight className="h-4 w-4 shrink-0 text-gray-400" />
        {planLine('New', selectedPlan, selectedPrice, true)}
      </div>

      <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
        {currentPlan && diff !== 0 && (
          <div className="flex items-center justify-between px-4 py-3 text-sm">
            <span className="text-gray-600">Price difference</span>
            <span className="font-medium text-gray-900">
              {diff > 0 ? '+' : '-'}{formatMoney(Math.abs(diff))}/mo
            </span>
          </div>
        )}
        {!isDowngrade && (
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-gray-600">Charged today</span>
            {previewLoading ? (
              <Skeleton className="h-5 w-16" />
            ) : preview ? (
              <span className="text-lg font-semibold text-gray-900">{formatMoney(preview.amountDue, currency)}</span>
            ) : (
              <span className="text-sm font-medium text-gray-900">Prorated difference</span>
            )}
          </div>
        )}
        <div className="px-4 py-3 text-xs text-gray-500">
          {isDowngrade ? (
            <>Your downgrade takes effect at the end of your current billing period{nextDate ? ` (${nextDate})` : ''}. You keep your current features until then.</>
          ) : preview ? (
            <>
              {preview.prorationAmount !== 0 && (
                <>Includes a {formatMoney(Math.abs(preview.prorationAmount), currency)} {preview.prorationAmount > 0 ? 'proration' : 'credit'} for the rest of this period. </>
              )}
              {nextDate && <>Next renewal on {nextDate}.</>}
            </>
          ) : (
            <>You'll be charged the prorated difference today. Your new plan starts immediately.</>
          )}
        </div>
      </div>

      {needsPayment && !paymentMethodId && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Add a payment method</h3>
          {!isStripeConfigured ? (
            <Alert variant="destructive">
              <AlertDescription>
                Payment system is temporarily unavailable. Please contact support.
              </AlertDescription>
            </Alert>
          ) : setupIntentSecret && options ? (
            <Elements stripe={stripePromise} options={options}>
              <PaymentMethodForm
                onPaymentReady={(id) => setPaymentMethodId(id)}
                onError={(error) => toast({ title: "Error", description: error, variant: "destructive" })}
                processing={processing}
              />
            </Elements>
          ) : (
            <Skeleton className="h-32 w-full rounded-xl" />
          )}
        </div>
      )}

      {needsPayment && paymentMethodId && (
        <p className={`flex items-center gap-2 rounded-xl p-3 text-sm ${tone.softNote}`}>
          <CheckCircle className="h-4 w-4" /> Payment method added
        </p>
      )}

      {(!needsPayment || paymentMethodId) && (
        <div className="space-y-2">
          <Button
            onClick={handleConfirm}
            disabled={processing}
            className={`h-12 w-full rounded-xl text-base font-semibold shadow-sm disabled:opacity-70 ${tone.cta} ${tone.ctaDisabled}`}
          >
            {processing ? (
              <>
                <div className={`mr-2 h-4 w-4 animate-spin rounded-full border-2 ${tone.spinner}`} />
                Processing...
              </>
            ) : isDowngrade ? (
              `Confirm downgrade to ${selectedPlan.name}`
            ) : (
              'Confirm upgrade'
            )}
          </Button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
            <Shield className="h-3.5 w-3.5" /> Billed to your card on file. Secured by Stripe.
          </p>
        </div>
      )}
    </div>
  );
}

interface BillingUpgradeModalProps {
  forceShow?: boolean;
  preselectedPlanId?: string;
  onClose?: () => void;
  /**
   * Which endpoint to call on confirm.
   * - 'upgrade' (default) -> POST /api/billing/upgrade
   * - 'reactivate' -> POST /api/billing/reactivate (canceled subscription flow)
   * Task 7 wires this through useUpgradeModal so ReactivationPage can open
   * the modal in reactivate mode.
   */
  mode?: 'upgrade' | 'reactivate';
  /** 'brand' renders CTAs and accents in the LimePhone brand palette (mobile). */
  accent?: 'default' | 'brand';
}

export function BillingUpgradeModal({
  forceShow = false,
  preselectedPlanId,
  onClose,
  mode = 'upgrade',
  accent = 'default',
}: BillingUpgradeModalProps = {}) {
  const tone: Tone = TONES[accent];
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlanForConfirmation, setSelectedPlanForConfirmation] = useState<Plan | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [successPlanName, setSuccessPlanName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedFeatures, setExpandedFeatures] = useState<Record<string, boolean>>({});
  // Forces the confirmation dialog into card-capture mode even when
  // upgradeContext reports a payment method on file. Used when the
  // server responds NEEDS_CARD (e.g. the stored PM was detached at
  // Stripe) so we swap in the SetupIntent form inline instead of
  // just toasting.
  const [forceNeedsCard, setForceNeedsCard] = useState(false);
  // attemptToken: stable per-attempt nonce composed into the server's
  // Stripe idempotency key. MUST be reused across a 3DS retry so Stripe
  // sees the same idempotency key and advances the intent state. We only
  // regenerate it after a terminal outcome (success, explicit cancel, or
  // a non-retryable error the user dismisses).
  const [attemptToken, setAttemptToken] = useState(() => crypto.randomUUID());
  // Last mutation payload — needed to replay on 3DS retry with the same
  // planId/paymentMethodId and the same attemptToken.
  const [lastAttempt, setLastAttempt] = useState<{ planId: string; paymentMethodId?: string } | null>(null);
  // Guard against an infinite REQUIRES_ACTION loop if a misbehaving gateway
  // re-challenges the same intent. Up to two client-side 3DS replays per
  // attempt — a billing-suspended account approves the outstanding invoice,
  // then the upgrade charge — but never the same PaymentIntent twice. Reset on
  // terminal outcomes and at the start of a new confirm.
  const [threeDsRetryCount, setThreeDsRetryCount] = useState(0);
  const confirmedIntents = useRef<Set<string>>(new Set());

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const hasToken = !!localStorage.getItem('token');

  const { data: upgradeContext, isLoading } = useQuery<UpgradeContext>({
    queryKey: ['/api/billing/upgrade-context'],
    staleTime: 0,
    gcTime: 0,
    enabled: hasToken
  });

  // Force reactivate endpoint when the subscription is known-canceled,
  // regardless of how the modal was opened. Otherwise CTAs scattered
  // across the app (sidebar, status bar, soft-gating) that default to
  // mode='upgrade' send canceled users into /api/billing/upgrade which
  // 400s with requiresReactivation — the exact bug users keep hitting.
  const subStatus = upgradeContext?.currentSubscription?.status;
  const isCanceledSub = subStatus === 'canceled' || subStatus === 'cancelled' || subStatus === 'churned';
  const effectiveMode: 'upgrade' | 'reactivate' = mode === 'reactivate' || isCanceledSub ? 'reactivate' : 'upgrade';
  const endpoint = effectiveMode === 'reactivate' ? '/api/billing/reactivate' : '/api/billing/upgrade';
  const action = effectiveMode === 'reactivate' ? 'reactivate' : 'upgrade';

  // Invalidate all caches that reflect subscription/plan state and close the modal.
  const invalidateBillingCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['/api/billing'] });
    queryClient.invalidateQueries({ queryKey: ['/api/billing/upgrade-context'] });
    // Critical: Invalidate organization queries to update planType/lifecycle
    queryClient.invalidateQueries({ queryKey: ['/api/organizations'] });
  };

  // Rotate the attempt token so the next confirm uses a fresh nonce. Called
  // after terminal outcomes (success, ALREADY_ACTIVE silent redirect, or
  // after the user dismisses a non-retryable error and re-attempts).
  const rotateAttemptToken = () => {
    setAttemptToken(crypto.randomUUID());
    setLastAttempt(null);
    setThreeDsRetryCount(0);
    confirmedIntents.current.clear();
  };

  const upgradeMutation = useMutation({
    mutationFn: async (data: { planId: string; paymentMethodId?: string }) => {
      // Client-side nonce component. The server prefixes organizationId
      // from the session (see server/lib/reactivateHandler.ts), so the
      // client key just needs to be unique-per-attempt.
      const idempotencyKey = `${action}:${data.planId}:${attemptToken}`;
      // Also include attemptToken in the body so the server can fall back
      // to it if any proxy strips the X-Idempotency-Key header.
      return apiRequest('POST', endpoint, { ...data, attemptToken }, { idempotencyKey });
    },
    onSuccess: (data: any) => {
      invalidateBillingCaches();
      // An upgrade's plan row flips from Stripe's subscription webhook, a
      // moment after the charge — refresh once more to pick it up.
      setTimeout(invalidateBillingCaches, 4000);

      if (data?.code === 'PAYMENT_PROCESSING') {
        toast({ title: 'Payment processing', description: data.message });
        setShowConfirmation(false);
        setSelectedPlanForConfirmation(null);
        rotateAttemptToken();
        return;
      }

      toast({
        title: effectiveMode === 'reactivate' ? 'Subscription Reactivated' : 'Plan Updated!',
        description: effectiveMode === 'reactivate'
          ? 'Your subscription is active again.'
          : 'Your subscription has been changed successfully.',
      });

      // Show the in-sheet success state; its Done button router-navigates to
      // /dashboard (avoids racing the query-cache invalidations above).
      setSuccessPlanName(selectedPlanForConfirmation?.name || 'your new plan');
      setShowConfirmation(false);
      setSelectedPlanForConfirmation(null);
      setForceNeedsCard(false);
      rotateAttemptToken();
    },
    onError: async (err: any) => {
      // queryClient.ts attaches the parsed JSON body at err.data. The
      // server returns { code, message, requestId, details? } — so we
      // read err.data.code (NOT err.code). See throwIfResNotOk in
      // client/src/lib/queryClient.ts.
      const body = err?.data as BillingErrorBody | undefined;
      const code = body?.code;

      // 3DS / SCA required — confirm the PaymentIntent client-side, then
      // retry the mutation with the SAME attemptToken so Stripe's
      // idempotency cache advances the existing intent rather than
      // creating a duplicate.
      if (code === 'REQUIRES_ACTION' && body?.details?.clientSecret) {
        // Bail out if we already replayed once — prevents an infinite loop
        // when a gateway re-challenges the same intent.
        const intentKey = String(body.details.paymentIntentId || body.details.clientSecret);
        if (threeDsRetryCount >= 2 || confirmedIntents.current.has(intentKey)) {
          setError('Verification could not be completed. Please try a different card.');
          toast({
            title: 'Verification failed',
            description: 'We could not complete the card verification. Please try a different card.',
            variant: 'destructive',
          });
          return;
        }
        const stripe = await stripePromise;
        if (!stripe) {
          setError('Payment system failed to load. Please refresh and try again.');
          toast({
            title: 'Payment system unavailable',
            description: 'Please refresh the page and try again.',
            variant: 'destructive',
          });
          return;
        }
        const { error: confirmError } = await stripe.confirmCardPayment(body.details.clientSecret);
        if (confirmError) {
          // Abandoned or refused: nothing changed (an upgrade only applies once
          // paid; the pending change expires by itself).
          setError(`${confirmError.message || 'Card verification failed'}${effectiveMode === 'upgrade' ? ' Your plan has not changed.' : ''}`);
          toast({
            title: 'Verification failed',
            description: confirmError.message || 'Please try a different card.',
            variant: 'destructive',
          });
          return;
        }
        // Replay with same payload + same attemptToken. Clear any stale
        // "declined" alert — the user just passed the challenge.
        confirmedIntents.current.add(intentKey);
        if (lastAttempt) {
          setError(null);
          setThreeDsRetryCount(c => c + 1);
          upgradeMutation.mutate(lastAttempt);
        }
        return;
      }

      // No / invalid card on file — swap the confirmation dialog into
      // SetupIntent capture mode. ConfirmationDialog already knows how
      // to mount Stripe Elements + PaymentElement when needsPayment is
      // true; we just force that flag and clear any stale captured id.
      // User adds card → mutation retries with the fresh paymentMethodId.
      if (code === 'NEEDS_CARD') {
        setForceNeedsCard(true);
        setError(null);
        toast({
          title: 'Add a payment method to continue',
          description: 'Enter your card below to finish reactivating.',
        });
        return;
      }

      // Already active (concurrent reactivate) — silent redirect.
      if (code === 'ALREADY_ACTIVE') {
        invalidateBillingCaches();
        setShowConfirmation(false);
        setSelectedPlanForConfirmation(null);
        setForceNeedsCard(false);
        rotateAttemptToken();
        setLocation('/dashboard');
        return;
      }

      // A decline is terminal for this attempt: the next try (a different
      // card) must be a NEW attempt, or the server's Stripe idempotency key
      // replays the cached decline.
      if (code === 'CARD_DECLINED') rotateAttemptToken();

      // CARD_DECLINED, LIFECYCLE_BLOCKED, PLAN_UNAVAILABLE, INTERNAL, unknown.
      const msg = body?.message || err?.message || 'Failed to change plan';
      const title =
        code === 'CARD_DECLINED' ? 'Card declined' :
        effectiveMode === 'reactivate' ? 'Reactivation Failed' : 'Plan Change Failed';
      setError(msg);
      toast({
        title,
        description: msg,
        variant: 'destructive',
      });
    },
  });

  const handlePlanSelect = (plan: Plan) => {
    setSelectedPlanForConfirmation(plan);
    setShowConfirmation(true);
  };

  const handleConfirm = (planId: string, paymentMethodId?: string) => {
    // Clear any stale error banner from a prior failed attempt before we
    // kick off a new one — otherwise the old Alert sits next to the new
    // spinner.
    setError(null);
    setThreeDsRetryCount(0);
    confirmedIntents.current.clear();
    const payload = { planId, paymentMethodId };
    setLastAttempt(payload);
    upgradeMutation.mutate(payload);
  };

  // Only treat subscription as current if it's active (not canceled).
  // Computed here rather than after the early returns below because the
  // preselect effect needs it.
  const hasActiveSubscription = upgradeContext?.currentSubscription?.status === 'active';
  const currentPlanId = hasActiveSubscription ? upgradeContext?.currentSubscription?.planId : null;

  // Honour preselectedPlanId: open straight onto the review step for the plan
  // the caller named. Every CTA that passes one (limit warnings, the power
  // dialer's upgrade panel, Plans.tsx after a requiresCheckout error) means
  // "this plan" -- the prop existed and was ignored, so all of them dropped
  // the user on the generic grid instead.
  //
  // Latched per id: if the user dismisses the review step they get the grid and
  // it does not spring back. Nothing is charged here -- the review step has its
  // own Confirm and pay button and a Cancel.
  const appliedPreselectRef = useRef<string | null>(null);
  useEffect(() => {
    if (!preselectedPlanId) {
      appliedPreselectRef.current = null;
      return;
    }
    if (appliedPreselectRef.current === preselectedPlanId) return;
    const plans = upgradeContext?.availablePlans;
    if (!plans?.length) return; // still loading; try again when it lands

    // Latch on the first real attempt, match or no match, so a miss falls back
    // to the grid once instead of re-resolving on every render.
    appliedPreselectRef.current = preselectedPlanId;

    const match = resolvePreselectedPlan(plans, preselectedPlanId);
    // No match, or it is the plan they are already on: the grid is the answer.
    if (!match || match.plan.id === currentPlanId) return;

    setBillingInterval(match.interval);
    setSelectedPlanForConfirmation(match.plan);
    setShowConfirmation(true);
  }, [preselectedPlanId, upgradeContext?.availablePlans, currentPlanId]);

  // NOTE: The component itself has no tests (vitest env is node, no jsdom);
  // the preselect resolution is covered by client/src/lib/planPreselect.test.ts
  // and the error-code contract by
  // server/lib/__tests__/reactivateHandler.test.ts. Manual smoke plan
  // lives in Task 8: (1) reactivate with valid card -> success + nav,
  // (2) reactivate with 3DS test card (4000 0025 0000 3155) -> REQUIRES_ACTION
  // then confirm -> success, (3) reactivate with declined card
  // (4000 0000 0000 0002) -> CARD_DECLINED toast.

  if (isLoading) {
    return (
      <Dialog open={true}>
        <DialogContent className="max-w-4xl">
          <DialogTitle className="sr-only">Select Plan</DialogTitle>
          <div className="space-y-4 p-4">
            <Skeleton className="h-8 w-48" />
            <div className="grid grid-cols-3 gap-4">
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!upgradeContext?.requiresUpgrade && !forceShow && !successPlanName) {
    return null;
  }

  const groupedPlans = groupPlansByName(upgradeContext?.availablePlans || []);
  const currentPlan = currentPlanId ? upgradeContext?.availablePlans.find(p => p.id === currentPlanId) : null;
  const isVoluntaryUpgrade = forceShow && !upgradeContext?.requiresUpgrade;

  return (
    <Dialog open={true} modal onOpenChange={(open) => !open && isVoluntaryUpgrade && onClose?.()}>
      <DialogContent
        className={`w-[calc(100%-1.5rem)] ${showConfirmation || successPlanName ? 'max-w-md' : 'max-w-4xl'} p-0 gap-0 overflow-hidden max-h-[92vh] rounded-2xl border-0 bg-white text-gray-900 shadow-xl`}
        onPointerDownOutside={(e) => isVoluntaryUpgrade ? undefined : e.preventDefault()}
        onEscapeKeyDown={(e) => isVoluntaryUpgrade ? undefined : e.preventDefault()}
      >
        <DialogTitle className="sr-only">
          {successPlanName ? 'Plan updated' : showConfirmation ? 'Review plan change' : 'Upgrade Your Plan'}
        </DialogTitle>

        {successPlanName ? (
          <div className="p-8 text-center space-y-4">
            <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${tone.successIconBg}`}>
              <CheckCircle className={`h-7 w-7 ${tone.successIcon}`} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {effectiveMode === 'reactivate' ? 'Subscription reactivated' : "You're all set"}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {effectiveMode === 'reactivate'
                  ? `Your ${successPlanName} plan is active again.`
                  : `Your plan is now ${successPlanName}.`}
              </p>
            </div>
            <Button
              onClick={() => { setSuccessPlanName(null); setLocation('/dashboard'); }}
              className={`h-11 w-full rounded-xl font-semibold ${tone.cta}`}
            >
              Done
            </Button>
          </div>
        ) : showConfirmation ? (
          <div className="overflow-y-auto max-h-[92vh]">
            {error && (
              <Alert variant="destructive" className="mx-5 mt-5 w-auto sm:mx-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <ReviewStep
              isOpen={showConfirmation}
              onBack={() => {
                setShowConfirmation(false);
                setSelectedPlanForConfirmation(null);
                setForceNeedsCard(false);
                setError(null);
              }}
              selectedPlan={selectedPlanForConfirmation}
              currentPlan={currentPlan || null}
              hasPaymentMethod={!forceNeedsCard && (upgradeContext?.paymentMethods?.length || 0) > 0}
              onConfirm={handleConfirm}
              processing={upgradeMutation.isPending}
              tone={tone}
            />
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[92vh] bg-gray-50">
            <div className="px-5 pt-5 pb-4 sm:px-6 sm:pt-6">
              {!isVoluntaryUpgrade && (
                <div className="mb-4 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
                  <h3 className="mb-1 font-semibold text-amber-900">
                    {upgradeContext?.trialStatus === 'expired'
                      ? 'Your trial has ended'
                      : upgradeContext?.currentSubscription?.status === 'canceled'
                      ? 'Your subscription was canceled'
                      : 'Upgrade required'}
                  </h3>
                  <p className="text-sm text-amber-800">
                    Choose a plan below to restore access to calling, messaging, and all features. Your data is safe.
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 pr-8 sm:pr-0">
                  <h2 className="text-xl font-semibold text-gray-900">Choose the right plan</h2>
                  <p className="text-sm text-gray-500">Change plans anytime. Unlimited inbound calls on every plan.</p>
                </div>
                <div className="flex shrink-0 items-center gap-1 self-start rounded-full bg-white p-1 shadow-sm ring-1 ring-gray-200 sm:self-auto">
                  {(['monthly', 'yearly'] as const).map((iv) => (
                    <button
                      key={iv}
                      onClick={() => setBillingInterval(iv)}
                      className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        billingInterval === iv ? tone.toggleOn : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {iv === 'monthly' ? 'Monthly' : 'Yearly'}
                      {iv === 'yearly' && (
                        <span className={`text-[10px] font-semibold ${billingInterval === iv ? tone.toggleSaveOn : tone.toggleSaveOff}`}>
                          Save up to 23%
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-5 pb-6 sm:px-6">
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {groupedPlans.map((group) => {
                  const annualOnly = ANNUAL_ONLY_PLAN_NAMES.has(group.name) && !!group.yearlyPlan;
                  const plan = (billingInterval === 'yearly' || annualOnly) && group.yearlyPlan
                    ? group.yearlyPlan
                    : group.monthlyPlan;

                  if (!plan) return null;

                  const monthlyDisplayPrice = getMonthlyDisplayPrice(plan);
                  const currentMonthlyPrice = getMonthlyDisplayPrice(currentPlan);
                  const isCurrent = plan.id === currentPlanId ||
                    group.monthlyPlan?.id === currentPlanId ||
                    group.yearlyPlan?.id === currentPlanId;
                  const isUpgrade = monthlyDisplayPrice > currentMonthlyPrice;
                  const isDowngrade = monthlyDisplayPrice < currentMonthlyPrice && currentMonthlyPrice > 0;
                  const isExpanded = expandedFeatures[group.name] || false;
                  const displayFeatures = isExpanded ? group.features : group.features.slice(0, FEATURE_PREVIEW_COUNT);
                  const yearlySavingsAmount = getYearlySavingsAmount(group);
                  const yearlyTotal = parseFloat(String(plan.basePrice || '0'));

                  return (
                    <div
                      key={group.name}
                      className={`relative flex flex-col rounded-2xl bg-white p-5 shadow-sm transition-shadow hover:shadow-md ${
                        isCurrent ? tone.currentRing : 'ring-1 ring-gray-200'
                      }`}
                    >
                      {isCurrent ? (
                        <span className={`absolute -top-2.5 left-5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${tone.currentBadge}`}>
                          Current plan
                        </span>
                      ) : group.isPopular ? (
                        <span className="absolute -top-2.5 left-5 rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-semibold text-white">
                          Most popular
                        </span>
                      ) : null}

                      <h3 className="text-base font-semibold text-gray-900">{group.name}</h3>
                      <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">
                        {getPlanPositioning(group.name, group.description)}
                      </p>

                      {annualOnly ? (
                        <>
                          <div className="mt-4 flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-gray-900">{formatMoney(yearlyTotal)}</span>
                            <span className="text-sm text-gray-500">/year</span>
                          </div>
                          <p className={`h-4 text-xs font-medium ${tone.accentText}`}>
                            Annual only · works out at {formatMoney(monthlyDisplayPrice)}/mo
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="mt-4 flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-gray-900">{formatMoney(monthlyDisplayPrice)}</span>
                            <span className="text-sm text-gray-500">/mo</span>
                          </div>
                          <p className={`h-4 text-xs font-medium ${tone.accentText}`}>
                            {plan.billingInterval === 'yearly'
                              ? `Billed ${formatMoney(yearlyTotal)} yearly${yearlySavingsAmount ? ` · save ${formatMoney(yearlySavingsAmount)}/yr` : ''}`
                              : ''}
                          </p>
                        </>
                      )}

                      <ul className="mt-4 flex-1 space-y-2">
                        {displayFeatures.map((feature, index) => feature.endsWith(':') ? (
                          <li key={index} className="pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {feature}
                          </li>
                        ) : (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${tone.check}`} />
                            <span>{sanitizeDashboardProviderCopy(feature)}</span>
                          </li>
                        ))}
                      </ul>
                      {group.features.length > FEATURE_PREVIEW_COUNT && (
                        <button
                          onClick={() => setExpandedFeatures(prev => ({ ...prev, [group.name]: !prev[group.name] }))}
                          className={`mt-2 flex items-center gap-1 self-start text-xs font-medium hover:underline ${tone.link}`}
                        >
                          {isExpanded ? (
                            <>Show less <ChevronUp className="h-3.5 w-3.5" /></>
                          ) : (
                            <>See all {group.features.filter((f) => !f.endsWith(':')).length} features <ChevronDown className="h-3.5 w-3.5" /></>
                          )}
                        </button>
                      )}

                      <div className="mt-5">
                        {isCurrent ? (
                          <div className="flex h-11 w-full items-center justify-center rounded-xl bg-gray-100 text-sm font-medium text-gray-500">
                            Your current plan
                          </div>
                        ) : (
                          <Button
                            onClick={() => handlePlanSelect(plan)}
                            className={`h-11 w-full rounded-xl font-semibold ${
                              isDowngrade
                                ? 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                                : tone.cta
                            }`}
                          >
                            {getPlanCta(group.name, isUpgrade, isDowngrade)}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
